import * as React from "react";
import { useMemo, useState, useEffect, useContext } from "react";
import {
  Button,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Alert,
} from "@mui/material/";
import { BreadCrumbPart, Page } from "../../components/layout/page";
import { GroupAdd, PersonAdd, Workspaces } from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { Urls } from "../../domain/urls";
import { WorkspacesList } from "./WorkspacesList";
import { OrganizationScopeSchema } from "../../api-client";
import { useWorkspaces } from "../../crud/workspaces/hooks";
import { useIsAnyKindOfAdmin, useIsOrgAdmin } from "../../crud/account/hooks";
import { OrganizationTeamGrid } from "./OrganizationMembers";
import {
  VerticalTabPanel,
  VerticalTabs,
} from "../../shared-components/VerticalTabs";
import { OrganizationSelector } from "../../shared-components/OrganizationSelector";
import { EmptyState } from "@/components/system/empty-state";
import { useOrganization } from "../../crud/organizations/hooks";
import { useOrganizationInvites } from "../../crud/invites/hooks";
import { UserContext } from "../../crud/user/context";
import { seatLimitAlertText } from "./util";

type AddButtonProps = {
  organizationId: number;
};

const AddButton = ({ organizationId }: AddButtonProps): React.ReactElement => {
  const { data: workspaces } = useWorkspaces({ organizationId });
  const isAdmin = useIsAnyKindOfAdmin(
    organizationId,
    workspaces?.map((w) => w.slug),
  );
  const isOrgAdmin = useIsOrgAdmin(organizationId);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  return (
    <>
      <Button onClick={handleClick}>Add</Button>
      <Menu onClose={handleClose} open={open} anchorEl={anchorEl}>
        <Tooltip
          arrow
          placement="right"
          title={
            !isAdmin
              ? "You must be an organization or workspace admin to add team mates."
              : ""
          }
        >
          <div>
            <MenuItem
              component={Link}
              disabled={!isAdmin}
              to={`/${Urls.Team}/${organizationId}/add-member`}
            >
              <ListItemIcon>
                <PersonAdd />
              </ListItemIcon>
              Teammate
            </MenuItem>
          </div>
        </Tooltip>
        <Tooltip
          arrow
          placement="right"
          title={
            !isOrgAdmin
              ? "You must be an organization admin to add workspaces."
              : ""
          }
        >
          <div>
            <MenuItem
              disabled={!isOrgAdmin}
              component={Link}
              to={`/${Urls.Team}/${organizationId}/create-workspace`}
            >
              <ListItemIcon>
                <Workspaces />
              </ListItemIcon>
              Workspace
            </MenuItem>
          </div>
        </Tooltip>
        <MenuItem component={Link} to={`/${Urls.Team}/create-organization`}>
          <ListItemIcon>
            <GroupAdd />
          </ListItemIcon>
          Organization
        </MenuItem>
      </Menu>
    </>
  );
};

export const TeamView = (): React.ReactElement => {
  const [selectedOrg, setSelectedOrg] = useState<
    OrganizationScopeSchema | undefined
  >(undefined);
  const { user } = useContext(UserContext);
  const [showDefaultWorkspaceBanner, setShowDefaultWorkspaceBanner] =
    useState(false);
  const location = useLocation();
  const breadCrumbs = useMemo<BreadCrumbPart[]>(() => {
    return selectedOrg
      ? [{ text: "Organization" }, { text: selectedOrg.name }]
      : [];
  }, [selectedOrg]);
  const { data: workspaces } = useWorkspaces({
    organizationId: selectedOrg?.id,
  });

  const { data: orgInvites } = useOrganizationInvites(selectedOrg?.id);
  const { data: organization } = useOrganization(selectedOrg?.id);

  const alertText = seatLimitAlertText(organization, orgInvites);

  // Remove the "show banner" query param from the URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("showDefaultWorkspaceBanner") === "true") {
      setShowDefaultWorkspaceBanner(true);
      // Remove the query param from the URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, [location]);

  return (
    <Page title="Team" breadCrumbs={breadCrumbs}>
      <Stack spacing={2}>
        {showDefaultWorkspaceBanner && (
          <Alert severity="info" sx={{ marginBottom: "1rem" }}>
            Your default workspace is still{" "}
            <strong>{user.defaultAccount.name}</strong>. You can change this in
            your <Link to="/profile">user profile</Link>.
          </Alert>
        )}
        {alertText && (organization?.memberLimit || 0) > 1 && (
          <Alert severity="warning" sx={{ marginBottom: "1rem" }}>
            {alertText}
          </Alert>
        )}
        <Stack direction="row" alignItems={"center"}>
          {selectedOrg && (
            <Tooltip
              open={workspaces?.length === 0}
              title={"Add a workspace to get started."}
              arrow
              placement="right"
            >
              <div>
                <AddButton organizationId={selectedOrg?.id} />
              </div>
            </Tooltip>
          )}
          <div style={{ marginLeft: "auto" }}>
            <OrganizationSelector onChange={(e) => setSelectedOrg(e)} />
          </div>
        </Stack>
        {selectedOrg && (
          <VerticalTabs tabsWidth="120px">
            <VerticalTabPanel value="Workspaces" withPaper={false}>
              {selectedOrg && (
                <WorkspacesList organizationId={selectedOrg.id} />
              )}
            </VerticalTabPanel>
            <VerticalTabPanel value="Team" withPaper={false}>
              {selectedOrg && (
                <OrganizationTeamGrid organizationId={selectedOrg.id} />
              )}
            </VerticalTabPanel>
          </VerticalTabs>
        )}
        {!selectedOrg && (
          <EmptyState message="Select an organization to view team information." />
        )}
      </Stack>
    </Page>
  );
};
